import React, { FC, useEffect, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { Trash2, Check, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "src/components/ui/button";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import VideoUploadProgress from "src/components/AssetUploaders/VideoUploadProgress";
import VideoProcessingProgress from "src/components/AssetUploaders/VideoProcessingProgress";
import { useVideoEncodingProgress } from "src/components/AssetUploaders/useVideoEncodingProgress";
import { useAzureVideoDownload } from "src/components/AssetUploaders/useAzureVideoDownload";
import EditMedia from "../EditMedia/EditMedia";
import MultiMediaUpload from "../MultiMediaUpload/MultiMediaUpload";
import get from "lodash.get";
import { useParams } from "react-router-dom";
import capitalize from "lodash.capitalize";

interface Props {
	stepNumber: number;
}

/**
 * Per-row status for a media item that's already been uploaded.
 * Image rows go straight to "Uploaded ✓". Video rows poll Bitmovin's
 * encoding progress until the m3u8 manifest is ready, then flip to
 * "Uploaded ✓". Each row is its own component instance so each video
 * gets its own poll loop.
 */
const MediaRowStatus: FC<{ reference: string; isVideo: boolean }> = ({
	reference,
	isVideo
}) => {
	// Pass empty string (not undefined) to match every other call site of
	// `useAzureVideoDownload` in the app — keeps behaviour consistent if
	// the hook ever distinguishes the two.
	const { url, processingStatus } = useAzureVideoDownload(
		"video",
		"",
		isVideo ? reference : null
	);
	// Only enable the encoding-progress poll while Bitmovin is genuinely
	// processing — not for "URL still negotiating" or "ReadyForPlayback".
	const encodingState = useVideoEncodingProgress(
		isVideo ? reference : null,
		isVideo && !url && processingStatus === "Processing"
	);

	if (isVideo && !url && processingStatus === "Processing") {
		return (
			<div className="flex flex-col items-stretch gap-1 min-w-[240px]">
				<VideoProcessingProgress state={encodingState} variant="inline" />
			</div>
		);
	}

	return (
		<div className="flex items-center gap-1 text-zinc-600">
			<Check size={20} />
			<span>Uploaded</span>
		</div>
	);
};

const MultiMediaUploadStep: FC<Props> = ({ stepNumber }) => {
	const { postId } = useParams<{ postId: string }>();
	const { control, setValue } = useFormContext<BannerFormState>();

	const [uuid, setUUID] = useState<string[]>([]);
	const [mediasPreview, setMediasPreview] = useState<Media[]>([]);

	const {
		done,
		error,
		uploading,
		upload,
		clear,
		videoUploadState,
		abortVideoUpload
	} = useAzureFileUpload((uuidList) => {
		if (!uuidList) return;

		if (Array.isArray(uuidList)) {
			setUUID((prevState) => [...prevState, ...uuidList]);
		}

		if (!Array.isArray(uuidList)) {
			setUUID((prevState) => [...prevState, uuidList]);
		}
	});

	const {
		done: editDone,
		error: editError,
		uploading: editUploading,
		upload: editUpload,
		clear: editClear,
		videoUploadState: editVideoUploadState,
		abortVideoUpload: abortEditVideoUpload
	} = useAzureFileUpload(
		() => {
			// skip
		},
		(uuidList, index) => {
			if (!uuidList && !index) return;

			const newIds = uuid.slice();

			if (Array.isArray(uuidList)) {
				newIds[index as number] = uuidList[0] as string;
			}

			if (!Array.isArray(uuidList)) {
				newIds[index as number] = uuidList as string;
			}

			setUUID(newIds);
		}
	);

	const deleteMedia = (fileId: string, index: number) => {
		setUUID(uuid.filter((id) => id !== fileId));

		setMediasPreview(mediasPreview.filter((_, i) => i !== index));

		if (medias?.length === 1) {
			setValue(`steps.${stepNumber - 1}.value`, String([]));
		}
	};

	const deleteAllMedia = () => {
		setUUID([]);
		setMediasPreview([]);
		setValue(`steps.${stepNumber - 1}.value`, "");
		clear();
		editClear();
	};

	const value = useWatch({
		name: `steps.${stepNumber - 1}.value`,
		control
	});

	const preview = useWatch({
		name: `steps.${stepNumber - 1}.preview`,
		control
	});

	const medias = useWatch({
		name: `steps.${stepNumber - 1}.mediasPreview`,
		control
	});

	const sanitizedMedias =
		medias && medias.filter((media) => media.preview && media.type);

	const note = useWatch({
		name: `steps.${stepNumber - 1}.note`,
		control
	});

	useEffect(() => {
		setValue(`steps.${stepNumber - 1}.mediasPreview`, mediasPreview);
	}, [mediasPreview]);

	useEffect(() => {
		const copyUUID = uuid.slice();
		const adaptLocalMediasPreview = mediasPreview.map(
			({ id, preview, file, type }, i) => {
				return {
					id,
					preview,
					file,
					type,
					value: copyUUID[i]
				};
			}
		);

		setValue(`steps.${stepNumber - 1}.mediasPreview`, adaptLocalMediasPreview);
	}, [uuid]);

	useEffect(() => {
		if (!postId || !sanitizedMedias) return;

		const getUUID = sanitizedMedias.map((file) => file.value);

		if (getUUID.length) {
			setUUID([...getUUID]);
			setMediasPreview(sanitizedMedias as Media[]);
		}
	}, [postId, preview]);

	const { errors } = useFormState({ control });

	const isAnswerInvalid =
		Boolean(get(errors, `steps[${stepNumber - 1}]`, null)) ||
		error ||
		editError;

	useEffect(() => {
		setValue(`steps.${stepNumber - 1}.error`, error || editError);
	}, [error, editError]);

	useEffect(() => {
		setValue(`steps.${stepNumber - 1}.uploading`, uploading || editUploading);
	}, [uploading, editUploading]);

	const isThereVideoUploaded =
		sanitizedMedias &&
		sanitizedMedias.some((media) => media.type.includes("video"));

	const isUploaded =
		!error &&
		!editError &&
		!uploading &&
		!editUploading &&
		((done && editDone) || value);

	return (
		<div>
			<div className="flex flex-col items-start justify-between gap-2">
				{medias && !medias.length && (
					<p
						className="text-sm font-medium text-zinc-600"
						aria-label="step-title"
					>
						{note}
					</p>
				)}

				{sanitizedMedias &&
					sanitizedMedias.map(({ file, value, id, type }, index) => {
						return (
							<div
								className="flex items-center justify-between w-full"
								key={id}
							>
								<div className="flex items-center gap-4">
									<p
										className="text-sm"
										style={{
											textOverflow: "ellipsis",
											maxWidth: 250,
											whiteSpace: "nowrap",
											overflow: "hidden"
										}}
									>
										{file ? file : capitalize(type)}
									</p>
									<div className="flex items-center gap-4">
										<EditMedia
											stepNumber={stepNumber}
											control={control}
											index={index}
											setMediasPreview={setMediasPreview}
											mediasPreview={mediasPreview}
											setValue={setValue}
											upload={editUpload}
											error={error || editError}
											uploading={uploading || editUploading}
											isVideo={
												typeof type === "string" && type.includes("video")
											}
										/>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											onClick={() => deleteMedia(value, index)}
											disabled={
												uploading || editUploading || error || editError
											}
										>
											<Trash2 size={18} />
										</Button>
									</div>
								</div>
								{isUploaded && value && (
									<MediaRowStatus
										reference={value}
										isVideo={typeof type === "string" && type.includes("video")}
									/>
								)}

								{(uploading || editUploading) &&
									// For video uploads, show the rich chunked-upload
									// progress UI (bar, %, MB/s, ETA, cancel). The hook
									// only populates `videoUploadState` for the video
									// path; for image uploads we keep the existing
									// spinner since single-shot images finish in one
									// shot and don't have meaningful progress data.
									(videoUploadState || editVideoUploadState ? (
										<VideoUploadProgress
											state={videoUploadState ?? editVideoUploadState}
											onCancel={
												videoUploadState
													? abortVideoUpload
													: abortEditVideoUpload
											}
											variant="inline"
										/>
									) : (
										<div className="flex items-center gap-1 text-zinc-600">
											<div className="animate-spin">
												<RefreshCw size={20} />
											</div>
											<span>Uploading</span>
										</div>
									))}
							</div>
						);
					})}

				{isThereVideoUploaded && (
					<p className="text-zinc-600" aria-label="step-title">
						Only one video can be added to the post
					</p>
				)}

				{(error || editError) && (
					<div className="flex items-center gap-1 text-destructive">
						<AlertTriangle size={18} />
						<span className="text-sm">
							Problem uploading the media(s). Please try again.
						</span>
					</div>
				)}
			</div>

			<div
				className={[
					"flex items-center justify-start gap-8 my-4",
					isAnswerInvalid || error ? "border border-destructive p-2" : ""
				].join(" ")}
			>
				<MultiMediaUpload
					stepNumber={stepNumber}
					control={control}
					error={error || editError}
					upload={(file: File, uploadType: string, uploadIndex?: number) =>
						upload(
							file,
							uploadType,
							uploadIndex,
							"retail-app-social-image-sponsored"
						)
					}
					uploading={(uploading || editUploading) && !done && !editDone}
					uuid={uuid}
					setMediasPreview={setMediasPreview}
					deleteAllMedia={deleteAllMedia}
					setValue={setValue}
				/>
			</div>
		</div>
	);
};

export default MultiMediaUploadStep;
